import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {jwtDecode} from "jwt-decode";

// Define the shape of each delete request data item
interface DeleteRequestData {
  id: number;
  addressId: number;
  userId: number;
  deleteReason: string;
  createdDate: string;
  deleteStatus: boolean;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  pinCode: string;
  blockCode: string;
  addressCode: string;
  latitude: string;
  longitude: string;
  userName: string;
  email: string;
  phoneNumber: number;
}

// Define your custom JWT payload interface (adjust if needed)
interface CustomJwtPayload {
  RoleId: string;
}

// A skeleton component to display during loading (using 7 columns in the table)
const DeleteRequestSkeleton = () => (
  <div className="animate-pulse">
    {/* Date Picker Skeleton */}
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-20 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
        <div className="flex items-end">
          <div className="h-10 w-full bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>

    {/* Table Skeleton */}
    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
      {/* Header Skeleton */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid grid-cols-7 gap-6 border-b">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="h-5 bg-gray-200 rounded"></div>
        ))}
      </div>

      {/* Rows Skeleton */}
      <div className="divide-y divide-gray-100">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="p-4 grid grid-cols-7 gap-6">
            {[...Array(7)].map((_, j) => (
              <div key={j} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ViewDeleteRequest: React.FC = () => {
  const navigate = useNavigate();

  // State to hold all fetched delete requests
  const [allDeleteRequests, setAllDeleteRequests] = useState<DeleteRequestData[]>([]);
  // State for delete requests to display (could be filtered by date)
  const [displayedRequests, setDisplayedRequests] = useState<DeleteRequestData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Check authentication and authorization on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") {
          toast.error("Unauthorized access: RoleId is not 1");
          navigate("/");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Invalid token. Please log in again.");
        navigate("/");
      }
    }
  }, [navigate]);

  // Fetch all delete requests from the API
  const fetchDeleteRequests = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(
        "https://pamsapi.vofoxsolutions.com/api/Admin/GetAllDeleteRequestsAsync",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch delete requests: ${response.status}`);
      }
      const data: DeleteRequestData[] = await response.json();
      setAllDeleteRequests(data);
      setDisplayedRequests(data);
    } catch (error) {
      console.error("Error fetching delete requests:", error);
      toast.error("Error fetching delete requests.");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter the delete requests by the provided date range (using createdDate)
  const filterDeleteRequestsByDate = () => {
    if (!startDate || !endDate) {
      if (!toast.isActive("select-dates")) {
        toast.info("Please select a start date and end date.", { toastId: "select-dates" });
      }
      return;
    }

    // Convert selected dates to Date objects for comparison
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filtered = allDeleteRequests.filter((request) => {
      // Assuming createdDate is in YYYY-MM-DD format
      const requestDate = new Date(request.createdDate);
      return requestDate >= start && requestDate <= end;
    });

    if (filtered.length === 0) {
      if(!toast.isActive("no-requests")){
      toast.info("No delete requests were submitted within the selected date range." , {toastId: "no-requests"});
      }
    }

    setDisplayedRequests(filtered);
  };

  // Fetch the delete requests when the component mounts
  useEffect(() => {
    fetchDeleteRequests();
  }, []);

  /**
   * Function: handleRowClick
   * ----------------------------------
   * When a row is clicked, this function uses the index of the clicked
   * delete request in the displayedRequests array to navigate to the
   * DeleteRequestDetails page. The request data is passed via state.
   */
  const handleRowClick = (index: number) => {
    const requestData = displayedRequests[index];
    navigate(`/delete-request-details/${index}`, { state: { deleteRequestData: requestData } });
};

  // Handle changes for the start date input
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    // If the end date is set and is before the new start date, clear it
    if (endDate && endDate < newStartDate) {
      setEndDate("");
    }
  };

  // Handle changes for the end date input
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  return (
    <div className="h-screen overflow-hidden bg-magnolia select-none">
      <div className="flex flex-col h-screen">
        <header className="flex items-center justify-between px-8 py-4 bg-white/90 backdrop-blur-sm shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
              Manage Delete Requests
            </h1>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto px-8 py-6">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <DeleteRequestSkeleton />
            ) : (
              <>
                <div className="flex items-center justify-between mb-8">
                  <h4 className="text-xl text-gray-800 font-semibold flex items-center gap-3">
                    Delete Request Management
                    {displayedRequests.length > 0 && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                        {displayedRequests.length} Records
                      </span>
                    )}
                  </h4>
                </div>

                {/* Date Picker Section */}
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Start Date</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">End Date</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        min={startDate}
                        max={new Date().toISOString().split("T")[0]}
                        disabled={!startDate}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={filterDeleteRequestsByDate}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        Fetch Delete Requests
                      </button>
                    </div>
                  </div>
                </div>

                {/* Delete Requests Table */}
                {displayedRequests.length > 0 && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 grid grid-cols-6 gap-6 border-b">
                      {[
                        "User Name",
                        "Email",
                        "Phone Number",
                        "Created Date",
                        "Delete Reason",
                        "Address",
                        
                      ].map((header) => (
                        <div key={header} className="text-sm font-semibold text-gray-600 text-center">
                          {header}
                        </div>
                      ))}
                    </div>

                    {/* Table Body */}
                    <div className="divide-y divide-gray-100">
                      {displayedRequests.map((request, index) => (
                        <div
                          key={request.id}
                          className="p-4 grid grid-cols-6 gap-6 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150"
                          onClick={() => handleRowClick(index)}
                        >
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm break-words hover:line-clamp-none">
                              {request.userName}
                            </span>
                          </div>
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm break-words hover:line-clamp-none">
                              {request.email}
                            </span>
                          </div>
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm line-clamp-3 hover:line-clamp-none">
                              {request.phoneNumber}
                            </span>
                          </div>
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm line-clamp-3 hover:line-clamp-none">
                              {request.createdDate}
                            </span>
                          </div>
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm line-clamp-3 hover:line-clamp-none">
                              {request.deleteReason}
                            </span>
                          </div>
                          <div className="text-center font-semibold text-black">
                            <span className="text-sm line-clamp-3 hover:line-clamp-none">
                              {request.addressLine1}
                              <br />
                              {request.addressLine2 && (
                                <>
                                  {request.addressLine2}
                                  <br />
                                </>
                              )}
                              {request.addressLine3 && (
                                <>
                                  {request.addressLine3}
                                  <br />
                                </>
                              )}
                              ({request.pinCode})
                            </span>
                          </div>
                          {/* <div className="flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div> */}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          theme="light"
        />
      </div>
    </div>
  );
};

export default ViewDeleteRequest;
